// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Generic big-number + label presentation used by KPI widgets
// (HouseVisitsTotalWidget, PersonasTotalWidget). Pure presentational —
// caller computes the aggregate and passes it in.
//
// Loading and empty states are explicit so the widget Renderer doesn't
// need to branch around them. `null` / `undefined` values render as a
// placeholder dash, not 0, to distinguish "no data" from "zero".

// Module-level — Intl.NumberFormat is expensive to instantiate; hoist
// the default one so it isn't allocated on every render.
const defaultFormatter = new Intl.NumberFormat('nl-NL')

// Map a text-align value to the flex cross-axis alignment used to position
// the (column-stacked) number + labels. Flexbox is used instead of plain
// text-align so the number is positioned regardless of its inline/block box.
const ALIGN_TO_ITEMS = { left: 'flex-start', center: 'center', right: 'flex-end' }

/**
 * @param {object} props
 * @param {number|null|undefined} props.value - The aggregate number to display
 * @param {string} props.label - Display label below the number (e.g. "Huisbezoeken")
 * @param {string} [props.sublabel] - Optional secondary label (e.g. date range summary)
 * @param {boolean} [props.isLoading] - Show a loading placeholder
 * @param {(value: number) => string} [props.formatValue] - Custom formatter (default: locale number)
 * @param {'left'|'center'|'right'} [props.align] - Horizontal alignment of the number (overrides the CSS default).
 * @param {React.CSSProperties} [props.valueStyle] - Inline style applied to the number (e.g. a caller-configured font-size).
 * @returns {JSX.Element}
 */
const MetricVisualization = ({
    value,
    label,
    sublabel,
    isLoading = false,
    formatValue,
    align,
    valueStyle,
}) => {
    const formatter = formatValue || ((v) => defaultFormatter.format(v))

    const renderValue = () => {
        if (isLoading) return <span className="metric-visualization-value-loading">…</span>
        if (value == null) return <span className="metric-visualization-value-empty">—</span>
        return <span className="metric-visualization-value-number" style={valueStyle}>{formatter(value)}</span>
    }

    return (
        <div
            className="metric-visualization"
            data-testid="metric-visualization"
            style={align ? { display: 'flex', flexDirection: 'column', alignItems: ALIGN_TO_ITEMS[align] || 'center', textAlign: align } : undefined}
        >
            <div className="metric-visualization-value">
                {renderValue()}
            </div>
            {label && <p className="metric-visualization-label">{label}</p>}
            {sublabel && <p className="metric-visualization-sublabel">{sublabel}</p>}
        </div>
    )
}

export default MetricVisualization
